import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectOption<T extends string | number> {
  value: T;
  label: string;
  disabled?: boolean;
  description?: string;
}

export interface SelectProps<T extends string | number = string> {
  value: T | null;
  onChange: (value: T | null) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  size?: SelectSize;
  label?: string;
  helperText?: string;
  error?: boolean | string;
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  /** naked: transparent trigger, no border/bg. For use inside FieldRow or similar. */
  variant?: 'default' | 'naked';
}

// ── Sizing (mirrors Input) ────────────────────────────────────────────────────

const H:  Record<SelectSize, string> = { sm: '28px', md: '32px', lg: '40px' };
const FS: Record<SelectSize, string> = { sm: '12px', md: '13px', lg: '14px' };
const PX: Record<SelectSize, string> = { sm: '8px',  md: '10px', lg: '12px' };

const LABEL_STYLE = {
  display: 'block' as const,
  marginBottom: '4px',
  fontSize: '11px',
  fontWeight: 500,
  color: 'var(--evo-fg-3)',
  fontFamily: 'var(--evo-font)',
} as const;

const HELPER_STYLE = {
  display: 'block' as const,
  marginTop: '4px',
  fontSize: '11px',
  fontFamily: 'var(--evo-font)',
} as const;

type MenuPos = { top: number; left: number; width: number };

// ── Chevron icon ──────────────────────────────────────────────────────────────

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width={12} height={12} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{
        flexShrink: 0,
        transform: open ? 'rotate(180deg)' : 'none',
        transition: 'transform 120ms linear',
      }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ── Close icon ────────────────────────────────────────────────────────────────

function CloseIcon() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ── Select ────────────────────────────────────────────────────────────────────

export function Select<T extends string | number = string>({
  value,
  onChange,
  options,
  placeholder,
  size = 'md',
  label,
  helperText,
  error,
  searchable = false,
  clearable = false,
  disabled = false,
  fullWidth = false,
  variant = 'default',
}: SelectProps<T>) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.selectPlaceholder');
  const isNaked = variant === 'naked';
  const [open, setOpen]           = useState(false);
  const [query, setQuery]         = useState('');
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const [focused, setFocused]     = useState(false);
  const [menuPos, setMenuPos]     = useState<MenuPos | null>(null);

  const triggerRef  = useRef<HTMLDivElement>(null);
  const menuRef     = useRef<HTMLDivElement>(null);
  const searchRef   = useRef<HTMLInputElement>(null);

  const hasError = !!error;
  const errorMsg = typeof error === 'string' ? error : undefined;
  const selectedOpt = options.find(o => o.value === value) ?? null;

  const filtered = searchable && query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  // ── Position ──────────────────────────────────────────────────────────────

  function computePos(): MenuPos | null {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    return { top: rect.bottom + 4, left: rect.left, width: rect.width };
  }

  useLayoutEffect(() => {
    if (!open) { setMenuPos(null); return; }
    setMenuPos(computePos());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    function reposition() { setMenuPos(computePos()); }
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Click outside ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !menuRef.current?.contains(t)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // ── Search focus & reset ──────────────────────────────────────────────────

  useEffect(() => {
    if (open && searchable) setTimeout(() => searchRef.current?.focus(), 0);
    if (!open) { setQuery(''); setFocusedIdx(-1); }
  }, [open, searchable]);

  // ── Keyboard ──────────────────────────────────────────────────────────────

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
    const enabled = filtered.filter(o => !o.disabled);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIdx(i => Math.min(i + 1, enabled.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && focusedIdx >= 0) {
      e.preventDefault();
      const opt = enabled[focusedIdx];
      if (opt) { onChange(opt.value); setOpen(false); }
    }
  }

  // ── Border / shadow ───────────────────────────────────────────────────────

  const borderColor = hasError
    ? 'var(--evo-danger)'
    : (open || focused) ? 'var(--evo-cyan)'
    : 'var(--evo-stroke)';
  const shadow = (open || focused)
    ? `0 0 0 1px ${hasError ? 'var(--evo-danger)' : 'var(--evo-cyan)'}`
    : 'none';

  // ── Menu portal ───────────────────────────────────────────────────────────

  const menu = open && menuPos ? createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: menuPos.top,
        left: menuPos.left,
        width: menuPos.width,
        background: 'var(--evo-panel)',
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        zIndex: 9999,
        overflow: 'hidden',
        backdropFilter: 'blur(8px)',
      }}
    >
      {searchable && (
        <div style={{ padding: '6px 8px 4px', borderBottom: '1px solid var(--evo-stroke)' }}>
          <input
            ref={searchRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setFocusedIdx(-1); }}
            placeholder={t('common.search')}
            style={{
              width: '100%', height: '26px', padding: '0 8px',
              background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)',
              borderRadius: '4px', color: 'var(--evo-fg-2)', outline: 'none',
              fontFamily: 'var(--evo-font)', fontSize: '12px', boxSizing: 'border-box',
            }}
          />
        </div>
      )}
      <div style={{ maxHeight: '280px', overflowY: 'auto', padding: '4px 0' }}>
        {filtered.length === 0 && (
          <div style={{
            padding: '16px', textAlign: 'center',
            fontSize: '13px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)',
          }}>
            No options
          </div>
        )}
        {filtered.map((opt, i) => {
          const isSelected = opt.value === value;
          const isFocused  = i === focusedIdx;
          return (
            <div
              key={String(opt.value)}
              onMouseEnter={() => !opt.disabled && setFocusedIdx(i)}
              onMouseDown={e => {
                e.preventDefault();
                if (opt.disabled) return;
                onChange(opt.value);
                setOpen(false);
              }}
              style={{
                padding: '8px 10px',
                cursor: opt.disabled ? 'not-allowed' : 'pointer',
                opacity: opt.disabled ? 0.5 : 1,
                background: isFocused ? 'var(--evo-bg-3)' : isSelected ? 'var(--evo-cyan-12)' : 'transparent',
                color: isSelected ? 'var(--evo-cyan)' : 'var(--evo-fg-2)',
                fontFamily: 'var(--evo-font)',
                fontSize: '13px',
                transition: 'background 80ms',
                margin: '0 4px',
                borderRadius: '4px',
              }}
            >
              <div style={{ lineHeight: 1.3 }}>{opt.label}</div>
              {opt.description != null && (
                <div style={{ fontSize: '11px', color: 'var(--evo-fg-3)', marginTop: '2px', lineHeight: 1.3 }}>
                  {opt.description}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>,
    document.body,
  ) : null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={isNaked ? { display: 'flex', flex: 1, minWidth: 0 } : { display: fullWidth ? 'block' : 'inline-block', width: fullWidth ? '100%' : 'auto' }}>
      {!isNaked && label != null && <label style={LABEL_STYLE}>{label}</label>}
      <div
        ref={triggerRef}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={() => { if (!disabled) setOpen(o => !o); }}
        onKeyDown={handleKeyDown}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          height: isNaked ? 'auto' : H[size],
          width: '100%',
          paddingLeft: isNaked ? 0 : PX[size],
          paddingRight: PX[size],
          background: isNaked ? 'transparent' : 'var(--evo-bg-3)',
          border: isNaked ? 'none' : `1px solid ${borderColor}`,
          borderRadius: 'var(--evo-r)',
          boxShadow: isNaked ? 'none' : shadow,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'border-color 120ms linear, box-shadow 120ms linear',
          boxSizing: 'border-box',
          outline: 'none',
          userSelect: 'none',
          minWidth: isNaked ? 0 : 120,
        }}
      >
        <span style={{
          flex: 1,
          fontSize: FS[size],
          fontFamily: 'var(--evo-font)',
          color: selectedOpt ? 'var(--evo-fg-2)' : 'var(--evo-fg-4)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1,
        }}>
          {selectedOpt?.label ?? resolvedPlaceholder}
        </span>

        {clearable && value != null && (
          <span
            role="button"
            aria-label={t('common.clear')}
            onMouseDown={e => { e.stopPropagation(); onChange(null); }}
            style={{
              display: 'inline-flex',
              flexShrink: 0,
              color: 'var(--evo-fg-3)',
              lineHeight: 0,
              padding: '2px',
              borderRadius: '3px',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLSpanElement).style.color = 'var(--evo-fg-2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLSpanElement).style.color = 'var(--evo-fg-3)'; }}
          >
            <CloseIcon />
          </span>
        )}

        <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-3)', lineHeight: 0 }}>
          <Chevron open={open} />
        </span>
      </div>

      {!isNaked && (helperText != null || errorMsg != null) && (
        <span style={{ ...HELPER_STYLE, color: errorMsg != null ? 'var(--evo-danger)' : 'var(--evo-fg-3)' }}>
          {errorMsg ?? helperText}
        </span>
      )}

      {menu}
    </div>
  );
}
